import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { ChangeEvent, Fragment } from 'react';
import {
  ActiveTableFilterOption,
  CustomFilterMap,
  TableFilter,
  TableFilterOption,
} from '@employ-engage/data';
import classNames from 'classnames';

export interface FilterPopoverProps {
  section: TableFilter;
  activeFilters: ActiveTableFilterOption[];
  toggleFilter: (
    e: ChangeEvent<HTMLInputElement>,
    section: TableFilter,
    option: TableFilterOption
  ) => void;
  calculateChecked: (
    section: TableFilter,
    option: TableFilterOption
  ) => boolean;
  customFilterMap?: CustomFilterMap;
}

export const FilterPopover = ({
  section,
  activeFilters,
  toggleFilter,
  calculateChecked,
  customFilterMap,
}: FilterPopoverProps) => {
  const numSelectedFiltersForIndex = (section: TableFilter) => {
    return activeFilters.filter((filter) => filter.parentId === section.id)
      .length;
  };

  const numSelected = numSelectedFiltersForIndex(section);

  return (
    <Popover
      key={section.value}
      className="relative inline-block px-4 text-left"
    >
      <PopoverButton className="group inline-flex justify-center text-sm font-medium text-gray-700 hover:text-gray-900">
        <span>{section.value}</span>
        {numSelected ? (
          <span className="ml-1.5 rounded bg-gray-200 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-gray-700">
            {numSelected}
          </span>
        ) : null}
        <ChevronDownIcon
          className="-mr-1 ml-1 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
          aria-hidden="true"
        />
      </PopoverButton>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <PopoverPanel
          anchor={{
            to: 'bottom end',
          }}
          className={classNames(
            'w-fit absolute right-0 z-10 mt-2 pb-8 origin-top-right rounded-md bg-white p-4 shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none overflow-y-auto',
            section.panelCss
          )}
        >
          {section.isCustomFilter && customFilterMap ? (
            customFilterMap[section.id]()
          ) : (
            <form className="space-y-4">
              {section.options.map((option, optionIdx) => (
                <div key={option.value} className="flex items-center">
                  <input
                    id={`filter-${section.id}-${optionIdx}`}
                    name={`${section.id}[]`}
                    defaultValue={option.value}
                    type="checkbox"
                    checked={calculateChecked(section, option)}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    onChange={(e) => toggleFilter(e, section, option)}
                  />
                  <label
                    htmlFor={`filter-${section.id}-${optionIdx}`}
                    className="ml-3 whitespace-nowrap pr-6 text-sm font-medium text-gray-900"
                  >
                    {option.label}
                  </label>
                </div>
              ))}
            </form>
          )}
        </PopoverPanel>
      </Transition>
    </Popover>
  );
};
